import { useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import {
  appendWasrapport,
  fetchWasserijItem,
  updateWasserijItem,
  type WasserijItemDetail,
  type WasserijItemPatch,
} from '../../lib/wasserij-items';
import { ITEM_STATUS_COLORS } from '../../lib/order-constants';
import { formatCurrency, formatDateTime } from '../../lib/format';
import { KleedCard } from '../item-detail/KleedCard';
import { SnijplanSection } from '../item-detail/SnijplanSection';
import { KleedTijdlijn } from './KleedTijdlijn';
import { MoveWasserijItemDialog } from './MoveWasserijItemDialog';
import { BerichtThread } from '../berichten/BerichtThread';
import { KlantlinkKopieren } from '../KlantlinkKopieren';
import { useOrderDetailDrawerStore } from '../../stores/order-detail-drawer-store';

interface Props {
  itemId: string;
  onClose: () => void;
  onSaved?: () => void;
}

export function WasserijItemDetailView({ itemId, onClose, onSaved }: Props) {
  const [item, setItem] = useState<WasserijItemDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  // Workflow draft (Status + checkboxes)
  const [status, setStatus] = useState('');
  const [voorinspectieGedaan, setVoorinspectieGedaan] = useState(false);
  const [klaarVoorAflevering, setKlaarVoorAflevering] = useState(false);

  // Save-state voor workflow
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Edit-states voor inline secties
  const [editOpmerkingen, setEditOpmerkingen] = useState(false);
  const [opmerkingenDraft, setOpmerkingenDraft] = useState('');
  const [opmerkingenSaving, setOpmerkingenSaving] = useState(false);
  const [opmerkingenError, setOpmerkingenError] = useState<string | null>(null);

  const [editWasrapport, setEditWasrapport] = useState(false);
  const [wasrapportDraft, setWasrapportDraft] = useState('');
  const [wasrapportSaving, setWasrapportSaving] = useState(false);
  const [wasrapportError, setWasrapportError] = useState<string | null>(null);

  const [voorinspectieExpanded, setVoorinspectieExpanded] = useState(false);

  const [moveDialogOpen, setMoveDialogOpen] = useState(false);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setNotFound(false);
    fetchWasserijItem(itemId)
      .then((data) => {
        if (cancelled) return;
        setItem(data);
        setStatus(data.status);
        setVoorinspectieGedaan(data.voorinspectieGedaan);
        setKlaarVoorAflevering(data.klaarVoorAflevering);
        setLoading(false);
      })
      .catch((err: Error & { status?: number }) => {
        if (cancelled) return;
        if (err.status === 404) setNotFound(true);
        else setError(err.message || 'Kon item niet laden');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [itemId]);

  useEffect(() => load(), [load]);

  const workflowPatch = useMemo<WasserijItemPatch>(() => {
    if (!item) return {};
    const patch: WasserijItemPatch = {};
    if (item.status !== status) patch.Status__c = status;
    if (item.voorinspectieGedaan !== voorinspectieGedaan) patch.Voorinspectie_gedaan__c = voorinspectieGedaan;
    if (item.klaarVoorAflevering !== klaarVoorAflevering) patch.Klaar_voor_aflevering__c = klaarVoorAflevering;
    return patch;
  }, [item, status, voorinspectieGedaan, klaarVoorAflevering]);
  const workflowDirty = Object.keys(workflowPatch).length > 0;

  const notifyUpdated = useCallback(() => {
    if (!item) return;
    window.dispatchEvent(new CustomEvent('wasserij-item:updated', { detail: { itemId: item.id } }));
    onSaved?.();
  }, [item, onSaved]);

  const handleSaveWorkflow = async () => {
    if (!item || !workflowDirty) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updateWasserijItem(item.id, workflowPatch);
      notifyUpdated();
      load();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  // ─── voorinspectie-opmerkingen edit (replace-style) ────────────────────

  const startEditOpmerkingen = () => {
    if (!item) return;
    setOpmerkingenDraft(item.voorinspectieOpmerkingen);
    setOpmerkingenError(null);
    setEditOpmerkingen(true);
  };

  const cancelEditOpmerkingen = () => {
    setEditOpmerkingen(false);
    setOpmerkingenError(null);
  };

  const saveOpmerkingen = async () => {
    if (!item) return;
    setOpmerkingenSaving(true);
    setOpmerkingenError(null);
    try {
      await updateWasserijItem(item.id, {
        Voorinspectie_Opmerkingen__c: opmerkingenDraft.trim(),
      });
      setEditOpmerkingen(false);
      notifyUpdated();
      load();
    } catch (err) {
      setOpmerkingenError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setOpmerkingenSaving(false);
    }
  };

  // ─── wasrapport append ─────────────────────────────────────────────────

  const startAddWasrapport = () => {
    setWasrapportDraft('');
    setWasrapportError(null);
    setEditWasrapport(true);
  };

  const cancelAddWasrapport = () => {
    setEditWasrapport(false);
    setWasrapportDraft('');
    setWasrapportError(null);
  };

  const saveWasrapport = async () => {
    if (!item || !wasrapportDraft.trim()) return;
    setWasrapportSaving(true);
    setWasrapportError(null);
    try {
      await appendWasrapport(item.id, wasrapportDraft.trim(), 'Office');
      setEditWasrapport(false);
      setWasrapportDraft('');
      notifyUpdated();
      load();
    } catch (err) {
      setWasrapportError(err instanceof Error ? err.message : 'Toevoegen mislukt');
    } finally {
      setWasrapportSaving(false);
    }
  };

  // ─── render ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6">
        <p className="text-sm text-slate-500">Item niet gevonden</p>
        <button type="button" onClick={onClose} className="text-sm text-indigo-600 hover:underline">
          Sluiten
        </button>
      </div>
    );
  }

  if (error || !item) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
          {error || 'Onbekende fout'}
        </div>
        <button type="button" onClick={onClose} className="text-sm text-indigo-600 hover:underline">
          Sluiten
        </button>
      </div>
    );
  }

  const voorinspectieVisible = voorinspectieExpanded
    ? item.voorinspectie
    : item.voorinspectie.slice(0, 5);

  return (
    <div className="flex flex-col h-full min-h-0 overflow-hidden">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-4 py-2.5 flex items-center gap-3 flex-shrink-0">
        <button
          type="button"
          onClick={onClose}
          aria-label="Sluiten"
          className="text-slate-400 hover:text-slate-600"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="text-sm font-semibold text-slate-900 truncate">
              {item.product.name || 'Wasserij item'}
            </h2>
            <span className="text-[11px] text-slate-400 font-mono">{item.itemNumber}</span>
          </div>
          {item.workOrderNumber && (
            <p className="text-xs text-slate-500 truncate">
              <button
                type="button"
                onClick={() => {
                  // Order-drawer (z-60) zit onder deze drawer (z-70): eerst
                  // sluiten, dan de order openen — anders opent hij onzichtbaar.
                  onClose();
                  useOrderDetailDrawerStore.getState().open(item.workOrderId);
                }}
                className="text-indigo-600 hover:underline"
                title="Open order-detail"
              >
                Order: {item.workOrderNumber}
              </button>
              {item.status && (
                <span className={clsx(
                  'ml-2 text-[10px] px-1.5 py-0.5 rounded font-medium',
                  ITEM_STATUS_COLORS[item.status] || 'bg-slate-100 text-slate-600'
                )}>{item.status}</span>
              )}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setMoveDialogOpen(true)}
          className="text-xs text-slate-600 hover:text-slate-900 hover:underline flex-shrink-0"
          title="Verplaats dit kleed naar een ander werkorder van dezelfde klant"
        >
          Verplaatsen
        </button>
        <a
          href={`/items/${item.id}`}
          className="text-xs text-slate-600 hover:text-slate-900 hover:underline flex-shrink-0"
          title="Open in volledig scherm"
        >
          Volledig ↗
        </a>
      </div>

      {/* Body */}
      <div data-drawer-scroll className="flex-1 overflow-y-auto p-4 space-y-4">

        {/* WORKFLOW — editable */}
        <Section title="Workflow">
          <Row label="Status">
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5"
            >
              <option value="">— kies status —</option>
              {item.picklists.status.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </Row>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={voorinspectieGedaan}
              onChange={(e) => setVoorinspectieGedaan(e.target.checked)}
              className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            Voorinspectie is gedaan
          </label>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={klaarVoorAflevering}
              onChange={(e) => setKlaarVoorAflevering(e.target.checked)}
              className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
            Klaar voor aflevering
          </label>
        </Section>

        {/* KLEED — Product/Soort/Merk/Vorm/Afmeting; bewerkbaar in één modus.
            Gedeelde component met de volledige /items/:id pagina. */}
        <KleedCard
          item={item}
          dense
          showPrice
          onSaved={() => {
            notifyUpdated();
            load();
          }}
        />

        {/* SNIJPLAN — alleen bij ondertapijt (WAS-UPSELL-KLEDA-BASE of SHS-ONDER-KLEDA-BASE) */}
        <SnijplanSection
          item={item}
          dense
          onSaved={() => {
            notifyUpdated();
            load();
          }}
        />

        {/* PAKKET & BEHANDELINGEN — alleen bij primary */}
        {item.product.isPrimary && (item.package || item.extras.length > 0) && (
          <Section title="Pakket &amp; behandelingen">
            {item.package && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-700">
                  <span className="text-xs text-slate-400 mr-2">Pakket</span>
                  {item.package.name}
                </span>
                <span className="text-slate-900">{formatCurrency(item.package.priceInclBtw)}</span>
              </div>
            )}
            {item.extras.length > 0 && (
              <div className="space-y-1 pt-2 border-t border-slate-100">
                <p className="text-xs text-slate-400">Extra behandelingen</p>
                {item.extras.map((ex) => (
                  <div key={ex.id} className="flex items-center justify-between text-sm">
                    <span className="text-slate-700">{ex.name}</span>
                    <span className="text-slate-900">{formatCurrency(ex.priceInclBtw)}</span>
                  </div>
                ))}
              </div>
            )}
          </Section>
        )}

        {/* VOORINSPECTIE — read-only chips + editable opmerkingen */}
        <Section title="Voorinspectie">
          {item.voorinspectie.length > 0 ? (
            <div className="flex flex-wrap gap-1.5">
              {voorinspectieVisible.map((v) => (
                <span
                  key={v}
                  className="text-[11px] bg-amber-50 text-amber-800 border border-amber-200 rounded px-2 py-0.5"
                  title={v}
                >
                  {v.length > 40 ? v.slice(0, 40) + '…' : v}
                </span>
              ))}
              {item.voorinspectie.length > 5 && !voorinspectieExpanded && (
                <button
                  type="button"
                  onClick={() => setVoorinspectieExpanded(true)}
                  className="text-[11px] text-indigo-600 hover:underline"
                >
                  +{item.voorinspectie.length - 5} meer ▾
                </button>
              )}
              {voorinspectieExpanded && item.voorinspectie.length > 5 && (
                <button
                  type="button"
                  onClick={() => setVoorinspectieExpanded(false)}
                  className="text-[11px] text-indigo-600 hover:underline"
                >
                  minder ▴
                </button>
              )}
            </div>
          ) : (
            <p className="text-xs text-slate-400 italic">Geen bevindingen</p>
          )}

          {/* Opmerkingen — EditableNotes-pattern */}
          <div className="pt-2 border-t border-slate-100">
            <div className="flex items-center justify-between mb-1">
              <p className="text-xs font-medium text-slate-500">Opmerkingen</p>
              {!editOpmerkingen && (
                <button
                  type="button"
                  onClick={startEditOpmerkingen}
                  className="text-xs text-indigo-600 hover:underline"
                >
                  {item.voorinspectieOpmerkingen ? 'Wijzigen' : 'Toevoegen'}
                </button>
              )}
            </div>
            {!editOpmerkingen ? (
              item.voorinspectieOpmerkingen ? (
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{item.voorinspectieOpmerkingen}</p>
              ) : (
                <p className="text-sm text-slate-400 italic">Geen opmerkingen</p>
              )
            ) : (
              <>
                <textarea
                  value={opmerkingenDraft}
                  onChange={(e) => setOpmerkingenDraft(e.target.value)}
                  rows={3}
                  autoFocus
                  className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {opmerkingenError && (
                  <p className="mt-1 text-xs text-red-600">{opmerkingenError}</p>
                )}
                <div className="flex items-center gap-2 mt-2">
                  <button
                    type="button"
                    onClick={saveOpmerkingen}
                    disabled={opmerkingenSaving}
                    className="text-xs font-medium px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {opmerkingenSaving ? 'Opslaan…' : 'Opslaan'}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEditOpmerkingen}
                    disabled={opmerkingenSaving}
                    className="text-xs text-slate-600 hover:text-slate-900"
                  >
                    Annuleren
                  </button>
                </div>
              </>
            )}
          </div>

          {item.voorinspectieRandPrijs != null && item.voorinspectieRandPrijs > 0 && (
            <div className="pt-2 border-t border-slate-100 flex items-center justify-between">
              <span className="text-xs text-slate-500">Rand-reparatie prijs</span>
              <span className="text-sm text-slate-900">{formatCurrency(item.voorinspectieRandPrijs)}</span>
            </div>
          )}
        </Section>

        {/* NA-INSPECTIE — read-only chips */}
        {item.naInspectie.length > 0 && (
          <Section title="Na-inspectie">
            <p className="text-[11px] text-slate-400 -mt-1">Ingevuld na reiniging door wasserij-medewerker</p>
            <div className="flex flex-wrap gap-1.5">
              {item.naInspectie.map((v) => (
                <span
                  key={v}
                  className="text-[11px] bg-slate-100 text-slate-700 border border-slate-200 rounded px-2 py-0.5"
                  title={v}
                >
                  {v.length > 50 ? v.slice(0, 50) + '…' : v}
                </span>
              ))}
            </div>
          </Section>
        )}

        {/* TIJDLIJN — fase-events + wasrapport-aanvulling, dezelfde lezer als de agents */}
        <Section title="Tijdlijn">
          <KleedTijdlijn itemId={item.id} compact />
        </Section>

        {/* WASRAPPORT — append-only log */}
        <Section
          title="Wasrapport"
          action={
            !editWasrapport ? (
              <button
                type="button"
                onClick={startAddWasrapport}
                className="text-xs text-indigo-600 hover:underline"
              >
                + Notitie toevoegen
              </button>
            ) : null
          }
        >
          <p className="text-[11px] text-slate-400 -mt-1">Intern audit-log, nieuwe entries bovenaan</p>
          {editWasrapport && (
            <div>
              <textarea
                value={wasrapportDraft}
                onChange={(e) => setWasrapportDraft(e.target.value)}
                rows={3}
                autoFocus
                placeholder="Notitie voor het wasrapport…"
                className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              {wasrapportError && (
                <p className="mt-1 text-xs text-red-600">{wasrapportError}</p>
              )}
              <div className="flex items-center gap-2 mt-2">
                <button
                  type="button"
                  onClick={saveWasrapport}
                  disabled={wasrapportSaving || !wasrapportDraft.trim()}
                  className="text-xs font-medium px-3 py-1.5 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50"
                >
                  {wasrapportSaving ? 'Toevoegen…' : 'Toevoegen'}
                </button>
                <button
                  type="button"
                  onClick={cancelAddWasrapport}
                  disabled={wasrapportSaving}
                  className="text-xs text-slate-600 hover:text-slate-900"
                >
                  Annuleren
                </button>
              </div>
            </div>
          )}
          {item.wasrapport ? (
            <pre className="mt-1 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded p-2 max-h-48 overflow-y-auto whitespace-pre-wrap font-mono leading-relaxed">
              {item.wasrapport}
            </pre>
          ) : (
            <p className="text-xs text-slate-400 italic">Nog geen entries</p>
          )}
        </Section>

        {/* BERICHTEN — bidirectionele communicatielijn office↔uitvoering voor dit item */}
        <BerichtThread
          werkOrderId={item.workOrderId}
          wasserijItemId={item.id}
          werkOrderName={item.workOrderNumber}
        />

        {/* MEDIA */}
        <Section title="Media &amp; info">
          {item.photosUrl ? (
            <a
              href={item.photosUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-indigo-600 hover:underline"
            >
              Foto's openen ↗
            </a>
          ) : (
            <p className="text-xs text-slate-400 italic">Geen foto's beschikbaar</p>
          )}
          {item.klantlinkUrl && (
            <div className="mt-2">
              <KlantlinkKopieren url={item.klantlinkUrl} />
            </div>
          )}
          <div className="text-xs text-slate-500 space-y-0.5 pt-2 border-t border-slate-100">
            <div>Aangemaakt: {formatDateTime(item.createdDate)}</div>
            <div>Laatst gewijzigd: {formatDateTime(item.lastModifiedDate)}</div>
            {item.mutatieBron && <div>Laatste mutatie via: {item.mutatieBron}</div>}
          </div>
        </Section>
      </div>

      {/* Footer — enkel voor workflow-save (andere sections hebben inline buttons) */}
      <div className="sticky bottom-0 z-10 bg-white border-t border-slate-200 px-4 py-3 flex items-center justify-between gap-3 flex-shrink-0">
        {saveError ? (
          <span className="text-xs text-red-600 truncate">{saveError}</span>
        ) : workflowDirty ? (
          <span className="text-xs text-slate-500">
            {Object.keys(workflowPatch).length} wijziging{Object.keys(workflowPatch).length === 1 ? '' : 'en'} in workflow
          </span>
        ) : (
          <span className="text-xs text-slate-400">Geen wijzigingen</span>
        )}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-slate-600 hover:text-slate-900 px-3 py-1.5"
            disabled={saving}
          >
            Sluiten
          </button>
          <button
            type="button"
            onClick={handleSaveWorkflow}
            disabled={!workflowDirty || saving}
            className={clsx(
              'px-3 py-1.5 text-xs font-medium rounded',
              !workflowDirty || saving
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            )}
          >
            {saving ? 'Opslaan…' : 'Opslaan'}
          </button>
        </div>
      </div>

      {moveDialogOpen && (
        <MoveWasserijItemDialog
          itemId={item.id}
          itemNumber={item.itemNumber}
          accountId={item.accountId}
          currentWorkOrderId={item.workOrderId}
          currentWorkOrderNumber={item.workOrderNumber}
          onClose={() => setMoveDialogOpen(false)}
          onMoved={() => {
            setMoveDialogOpen(false);
            notifyUpdated();
            load();
          }}
        />
      )}
    </div>
  );
}

function Section({
  title,
  action,
  children,
}: {
  title: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white rounded-lg border border-slate-200 p-3 space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wide">{title}</h3>
        {action}
      </div>
      <div className="space-y-2">{children}</div>
    </section>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-3 gap-2 items-center">
      <label className="text-xs text-slate-500 col-span-1">{label}</label>
      <div className="col-span-2">{children}</div>
    </div>
  );
}
